import { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from '../i18n';
import { analytics } from '../hooks/useAnalytics';
import { DirectionalIcon } from '../components/ui/DirectionalIcon';

const API_BASE = window.location.hostname.includes('lotngo.com')
  ? 'https://api.lotngo.com/api/v1'
  : 'http://localhost:3001/api/v1';

const COUNTRY_CODES: Record<string, string> = {
  country_israel: 'IL',
  country_usa: 'US',
  country_france: 'FR',
  country_germany: 'DE',
  country_uk: 'GB',
  country_other: 'OTHER',
};

export default function JoinBuildingPage() {
  const navigate = useNavigate();
  const { t, direction } = useTranslation();

  const [role, setRole] = useState<'resident' | 'hoa_member'>('resident');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [street, setStreet] = useState('');
  const [streetNumber, setStreetNumber] = useState('');
  const [country, setCountry] = useState('country_israel');
  const [cities, setCities] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    analytics.trackJoinBuildingPageViewed();
    fetch('/data-cities.json')
      .then((r) => r.json())
      .then((data: string[]) => setCities(data))
      .catch(() => {});
  }, []);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!fullName.trim() || !email.trim() || !city.trim()) {
      setError(t('fill_required'));
      return;
    }
    if (!emailRegex.test(email)) {
      setError(t('invalid_email'));
      return;
    }

    setLoading(true);
    analytics.trackJoinBuildingSubmitted(role);

    const nameParts = fullName.trim().split(/\s+/);
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ') || undefined;

    try {
      await axios.post(`${API_BASE}/leads`, {
        lead_type: role,
        first_name: firstName,
        last_name: lastName,
        email: email.trim(),
        phone_number: phone.trim() || undefined,
        city: city.trim(),
        street_name: street.trim() || undefined,
        street_number: streetNumber.trim() || undefined,
        country: COUNTRY_CODES[country] || 'IL',
      });

      setSuccess(true);
      analytics.trackJoinBuildingSuccess(role);
    } catch (err) {
      const message =
        axios.isAxiosError(err)
          ? (err.response?.data as { error?: { message?: string } })?.error?.message || t('submit_failed')
          : t('submit_failed');
      setError(message);
      analytics.trackJoinBuildingFailed(role);
    } finally {
      setLoading(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    border: 'none',
    background: 'var(--surface-low)',
    fontFamily: 'var(--inter)',
    fontSize: '14px',
    color: 'var(--on-surface)',
    outline: '2px solid transparent',
    transition: 'outline 0.15s',
    textAlign: direction === 'rtl' ? 'right' : 'left',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--on-surface-2)',
    display: 'block',
    marginBottom: '0.375rem',
    textAlign: direction === 'rtl' ? 'right' : 'left',
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
    appearance: 'none',
    WebkitAppearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%23666' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: direction === 'rtl' ? '12px center' : 'calc(100% - 12px) center',
    paddingInlineEnd: '2rem',
    direction: direction,
  };

  if (success) {
    return (
      <div dir={direction} style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--surface)',
        padding: '1.5rem',
      }}>
        <main style={{ width: '100%', maxWidth: '420px', textAlign: 'center' }}>
          <div style={{
            background: 'var(--surface-lowest)',
            borderRadius: '12px',
            padding: '3rem 2rem',
            boxShadow: 'var(--whisper)',
          }}>
            <span className="material-symbols-outlined" style={{
              fontSize: '56px',
              color: '#16a34a',
              marginBottom: '1rem',
              display: 'block',
              fontVariationSettings: "'FILL' 1",
            }}>check_circle</span>
            <h2 style={{
              fontFamily: 'var(--manrope)',
              fontSize: '1.25rem',
              fontWeight: 700,
              color: 'var(--on-surface)',
              margin: '0 0 0.5rem',
            }}>{t('success_title')}</h2>
            <p className="body-md" style={{ margin: 0 }}>
              {t('success_message')}
            </p>
          </div>
          <button
            onClick={() => navigate('/login')}
            style={{
              marginTop: '1.5rem',
              background: 'none',
              border: 'none',
              color: 'var(--primary)',
              fontFamily: 'var(--inter)',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem',
            }}
          >
            <DirectionalIcon icon="arrow_forward" />
            {t('back_to_login')}
          </button>
        </main>
      </div>
    );
  }

  return (
    <div dir={direction} style={{
      minHeight: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      background: 'var(--surface)',
      padding: '1.5rem',
      paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1.5rem)',
      paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)',
    }}>
      {/* Background blur blobs */}
      <div style={{
        position: 'fixed', zIndex: -1,
        top: '-10%', right: '-10%',
        width: '500px', height: '500px',
        background: 'rgba(0,91,192,0.05)',
        borderRadius: '50%', filter: 'blur(120px)',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'fixed', zIndex: -1,
        bottom: '-10%', left: '-10%',
        width: '500px', height: '500px',
        background: 'rgba(95,92,120,0.05)',
        borderRadius: '50%', filter: 'blur(120px)',
        pointerEvents: 'none',
      }} />

      <main style={{ width: '100%', maxWidth: '420px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem', marginTop: '1rem' }}>
          <h1 style={{
            fontFamily: 'var(--manrope)',
            fontSize: '1.5rem',
            fontWeight: 700,
            color: 'var(--on-surface)',
            margin: '0 0 0.5rem',
          }}>{t('page_title')}</h1>
          <p className="body-md" style={{ margin: 0, lineHeight: 1.5 }}>
            {t('page_subtitle')}
          </p>
        </div>

        {/* Form card */}
        <div style={{
          background: 'var(--surface-lowest)',
          borderRadius: '12px',
          padding: '1.5rem',
          boxShadow: 'var(--whisper)',
        }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Role */}
            <div>
              <label style={labelStyle}>{t('role_label')}</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as 'resident' | 'hoa_member')}
                style={selectStyle}
              >
                <option value="resident">{t('role_resident')}</option>
                <option value="hoa_member">{t('role_hoa_member')}</option>
              </select>
            </div>

            {/* Full name */}
            <div>
              <label style={labelStyle}>{t('full_name')} *</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder={t('name_placeholder')}
                required
                style={inputStyle}
              />
            </div>

            {/* Phone */}
            <div>
              <label style={labelStyle}>{t('phone')}</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={t('phone_placeholder')}
                style={{ ...inputStyle, direction: 'ltr', textAlign: 'left' }}
              />
            </div>

            {/* Email */}
            <div>
              <label style={labelStyle}>{t('email')} *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('email_placeholder')}
                required
                style={{ ...inputStyle, direction: 'ltr', textAlign: 'left' }}
              />
            </div>

            {/* Divider */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              margin: '0.25rem 0',
            }}>
              <div style={{ flex: 1, height: '1px', background: 'var(--outline-variant)' }} />
              <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--on-surface-2)' }}>
                {t('building_details')}
              </span>
              <div style={{ flex: 1, height: '1px', background: 'var(--outline-variant)' }} />
            </div>

            {/* City */}
            <div>
              <label style={labelStyle}>{t('city')} *</label>
              <input
                type="text"
                list="city-list"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder={t('city_placeholder')}
                required
                style={inputStyle}
              />
              <datalist id="city-list">
                {cities.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
              {cities.length > 0 && (
                <span style={{
                  fontSize: '11px',
                  color: 'var(--on-surface-3)',
                  marginTop: '2px',
                  display: 'block',
                  textAlign: direction === 'rtl' ? 'right' : 'left',
                }}>{t('city_hint')}</span>
              )}
            </div>

            {/* Street + Number */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: '0.5rem' }}>
              <div>
                <label style={labelStyle}>{t('street')}</label>
                <input
                  type="text"
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  placeholder={t('street_placeholder')}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>{t('street_number')}</label>
                <input
                  type="text"
                  value={streetNumber}
                  onChange={(e) => setStreetNumber(e.target.value)}
                  placeholder="42"
                  style={{ ...inputStyle, textAlign: 'center' }}
                />
              </div>
            </div>

            {/* Country */}
            <div>
              <label style={labelStyle}>{t('country')}</label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                style={selectStyle}
              >
                <option value="country_israel">{t('country_israel')}</option>
                <option value="country_usa">{t('country_usa')}</option>
                <option value="country_france">{t('country_france')}</option>
                <option value="country_germany">{t('country_germany')}</option>
                <option value="country_uk">{t('country_uk')}</option>
                <option value="country_other">{t('country_other')}</option>
              </select>
            </div>

            {/* Error */}
            {error && (
              <p style={{
                margin: 0,
                fontSize: '13px',
                color: 'var(--error)',
                textAlign: 'center',
              }}>{error}</p>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.875rem',
                background: loading ? 'var(--primary-container)' : 'var(--primary)',
                color: loading ? 'var(--on-primary-container)' : 'var(--on-primary)',
                border: 'none',
                borderRadius: '8px',
                fontFamily: 'var(--inter)',
                fontSize: '15px',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background 0.15s',
              }}
            >
              {loading ? t('submitting') : t('submit')}
            </button>
          </form>
        </div>

        {/* Back to login */}
        <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
          <button
            onClick={() => navigate('/login')}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--primary)',
              fontFamily: 'var(--inter)',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem',
            }}
          >
            <DirectionalIcon icon="arrow_forward" />
            {t('back_to_login')}
          </button>
        </div>
      </main>
    </div>
  );
}
